import { action, observable } from 'mobx';

import { ApiUrls } from '@app/AppConstants';
import { ApiV1Job, JobDetailsModel, JobParameterModel, JobSaveParametersModel, JobStatus, JobTriggerShortInfo } from '@app/Models/WebApiModels';
import ApiService from '@app/Services/ApiService';

import { modalService, ModalButtonType } from '@app/Components/Modal';
import { JobParametersDialog, JobParametersSubmitModel } from '../JobParametersDialog/JobParametersDialog';
import { JobItemProps } from './JobItem';

const CONFIRM_TIMEOUT_MS = 3000;

export default class JobItemStore {
    @observable errorToggle: boolean = false;
    @observable isStopping: boolean = false;
    @observable showAllTriggers: boolean = false;
    private _job: ApiV1Job;
    private _props: JobItemProps;

    constructor(props: JobItemProps) {
        this._job = props.job;
        this._props = props;
    }

    statusPending: JobStatus = JobStatus.Pending;

    @action
    toggleLastError() {
        this.errorToggle = !this.errorToggle;
    }

    @action.bound
    toggleShowAllTriggers() {
        this.showAllTriggers = !this.showAllTriggers;
    }

    @action
    async runJob() {
        const name = this._job.name ?? '';
        const { data: job } = await ApiService.get<JobDetailsModel>(ApiUrls.JobParametersUrl + '/' + name, null);

        const parameters = job.parameters;
        const title = `Run ${name} job with parameters`;
        if (parameters && parameters.length > 0) {
            await modalService.show(
                JobParametersDialog,
                {
                    showTriggerState: false,
                    jobData: job,
                    hasWriteAccess: this._props.hasWriteAccess,
                    hasRunAccess: this._props.hasRunAccess,
                    onSubmit: this._onSubmit
                },
                {
                    title: title
                }
            );
        } else {
            if (await modalService.showConfirmation(`Do you really want to run job ${name} now?`, { confirmTimeout: CONFIRM_TIMEOUT_MS })) {
                await this.runTriggerInternal([], name);
            }
        }
    }

    @action.bound
    setIsStopping(value: boolean) {
        this.isStopping = value;
    }

    @action.bound
    async stopJob() {
        const { name } = this._job;

        if (await modalService.showConfirmation(`Do you really want to stop job ${name} before it's over?`, { confirmTimeout: CONFIRM_TIMEOUT_MS })) {
            await ApiService.post(`${ApiUrls.JobStopUrl}/${name}`);
            this.setIsStopping(true);
        }
    }

    async showJobParams(triggerId: string = '') {
        const { name } = this._job;
        const trigger = (triggerId && this._job.triggers?.find((t) => t.name === triggerId)) || null;
        const { data } = await ApiService.get<JobDetailsModel>(ApiUrls.JobParametersUrl + '/' + name + '?triggerName=' + triggerId);

        await this._showJobParamsModal(data, trigger);
    }

    async showJobSettings() {
        const { name } = this._job;
        const { data } = await ApiService.get<JobDetailsModel>(ApiUrls.JobParametersUrl + '/' + name + '?isJobSettings=true');

        await this._showJobSettings(data);
    }

    async saveParams(jobName: string, payload: JobSaveParametersModel) {
        const { data } = await ApiService.post<ApiV1Job>(ApiUrls.JobParametersUrl + '/' + jobName, payload);

        this._job = data;
        this._props.onParametersSaved?.(data);
    }

    async saveSettings(jobName: string, payload: JobSaveParametersModel) {
        const { data } = await ApiService.post<ApiV1Job>(ApiUrls.JobSettingsUrl + '/' + jobName, payload);

        this._job = data;
        this._props.onSettingsSaved?.(data);
    }

    private async _showJobParamsModal(data: JobDetailsModel, trigger: JobTriggerShortInfo | null) {
        const title = 'Job Parameters: ' + ((trigger && trigger.name) || `New trigger for ${data.name}`);

        await modalService.show(
            JobParametersDialog,
            {
                showTriggerState: true,
                jobData: data,
                trigger: trigger,
                hasWriteAccess: this._props.hasWriteAccess,
                hasRunAccess: this._props.hasRunAccess,
                onSubmit: this._onSubmit
            },
            {
                title: title
            }
        );
    }

    private async _showJobSettings(data: JobDetailsModel) {
        const title = this._job.name + ' settings';

        await modalService.show(
            JobParametersDialog,
            {
                jobData: data,
                isJobSettings: true,
                showTriggerState: false,
                hasWriteAccess: this._props.hasWriteAccess,
                hasRunAccess: this._props.hasRunAccess,
                onSubmit: this._onSubmit
            },
            {
                title: title
            }
        );
    }

    @action.bound
    private async _onSubmit(button: ModalButtonType, result: JobParametersSubmitModel) {
        const jobName = result.jobName || '';
        const parameters = result.parameters || [];

        if (button === ModalButtonType.RunNow) {
            await this.runTriggerInternal(parameters, jobName);
        } else if (button === ModalButtonType.SaveAndRunNow) {
            await this.saveParams(jobName, result);
            await this.runTriggerInternal(parameters, jobName);
        } else if (button === ModalButtonType.Save && !result.isJobSettings) {
            await this.saveParams(jobName, result);
        } else if (button === ModalButtonType.Save && result.isJobSettings) {
            await this.saveSettings(jobName, result);
        } else if (button === ModalButtonType.CreateNew) {
            await this.createNewTrigger(result);
        } else if (button === ModalButtonType.Delete) {
            return await this.delete(result);
        } else if (button === ModalButtonType.Submit && result.parameters) {
            await this.runTriggerInternal(result.parameters, jobName);
        }
        return true;
    }

    @action
    async createNewTrigger(jobDetails: JobSaveParametersModel) {
        try {
            const { data } = await ApiService.post<ApiV1Job>(ApiUrls.JobTriggerCreateUrl, jobDetails);
            this._props.onTriggerCreated?.(data);
            this._job = data;
        } catch (conflict) {
            const { response: { data } } = conflict as { response: { data: { errors?: string[], error?: string } } };
            void modalService.showError(data.error || data.errors?.join(' ') || JSON.stringify(data));
            throw conflict;
        }
    }

    @action.bound
    async delete(jobDetails: JobSaveParametersModel) {
        if (!(await modalService.showConfirmation('Are you sure you want to delete this trigger?', { color: 'danger', confirmTimeout: CONFIRM_TIMEOUT_MS }))) {
            return false;
        }

        const triggerId = (jobDetails && jobDetails.triggerName) || '';
        const jobName = (jobDetails && jobDetails.jobName) || '';
        const { data: job } = await ApiService.delete<ApiV1Job>(ApiUrls.JobTriggerDeleteUrl + '?triggerId=' + triggerId + '&jobName=' + jobName);
        this._job = job;
        this._props.onTriggerDeleted?.(job);
        return true;
    }

    @action
    async runTriggerInternal(params: JobParameterModel[], name: string) {
        this.errorToggle = false;
        const { data } = await ApiService.post<ApiV1Job>(ApiUrls.JobStartUrl + '/' + name, { parameters: params });

        this._job = data;
        this._props.onJobStarted?.(data);
    }
}
